/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, focusBoxShadow, HFlow, Icon, isEmpty, Tag, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { SubtipoRecord } from 'components/form/field/select/SubtipoAtendimentoSelectField/SubtipoAtendimentoSelectField'
import { format } from 'date-fns'
import {
  OrigemAtendimento,
  SubtipoAtendimentoHistoricoClinico,
  TipoAtendimentoHistoricoClinico,
  TipoAtendimentoProfissional,
} from 'graphql/types.generated'
import { Fragment, useMemo } from 'react'
import { AccordionItem, AccordionItemButton, AccordionItemPanel, AccordionItemState } from 'react-accessible-accordion'
import { turnoRecord } from 'types/enums'
import { stringGrouper } from 'util/string/string-grouper'

import {
  accordionBloqueadoMensagem,
  HistoricoCabecalhoModel,
  recordEscutaHistoricoTagStyle,
  tipoAtendimentoHistoricoData,
} from '../../model/historicoModel'
import {
  hasSomeSubmodulesPlano,
  historicoIsImpossibleToPrint,
  mountDadosClinicosHeader,
  pluralizeAndConcatValue,
} from '../../utils/historicoUtils'
import HistoricoAvaliacaoElegibilidadePanel from './detail/avaliacaoElegibilidade/HistoricoAvaliacaoElegibilidadePanel'
import HistoricoIdadeOrigemAtendimento from './detail/HistoricoIdadeOrigemAtendimento'
import HistoricoInfoAdminPanel from './detail/HistoricoInfoAdminPanel'
import HistoricoMarcadoresConsumoAlimentarPanel from './detail/marcadoresConsumoAlimentar/HistoricoMarcadoresConsumoAlimentarPanel'
import HistoricoVacinacaoPanel from './detail/vacinacao/HistoricoVacinacaoPanel'
import HistoricoVisitaDomiciliarPanel from './detail/visitaDomiciliar/HistoricoVisitaDomiciliarPanel'
import HistoricoZikaPanel from './detail/zika/HistoricoZikaPanel'

export interface HistoricoAccordionProps {
  historico: HistoricoCabecalhoModel
  dataNascimentoCidadao: LocalDate
  cpfOuCnsCidadao: string
}

export default function HistoricoAccordion(props: HistoricoAccordionProps) {
  const { historico, dataNascimentoCidadao, cpfOuCnsCidadao } = props
  const theme = useTheme()
  const isImpossibleToPrint = historicoIsImpossibleToPrint(historico)
  const hasCondicoesAvaliadas = !isEmpty(historico.condicoesAvaliadas)
  const dadosClinicosHeader = mountDadosClinicosHeader(historico)
  const dataAtendimento = format(historico.dataAtendimento, 'yyyy-MM-dd')

  const horaOrTurnoByOrigem =
    historico.origemAtendimento === OrigemAtendimento.PEC
      ? format(historico.dataAtendimento, 'HH:mm')
      : turnoRecord[historico.turno]

  const tipoAtendimento = historico.tipoAtendProf ?? historico.tipoApresentacao

  const tipoAtendimentoHistorico = tipoAtendimentoHistoricoData[tipoAtendimento.toString()]

  const subtipoAtendimento = tipoAtendimentoHistorico.tags?.find(
    (subtipo) => subtipo.id === historico.coSubtipoAtendimento
  )

  const isVisitaAusenteOuRecusada =
    tipoAtendimento === TipoAtendimentoHistoricoClinico.VISITA_DOMICILIAR &&
    (subtipoAtendimento.nome === SubtipoAtendimentoHistoricoClinico.CIDADAO_AUSENTE ||
      subtipoAtendimento.nome === SubtipoAtendimentoHistoricoClinico.VISITA_RECUSADA)

  const classes = useMemo(() => styles(theme, tipoAtendimentoHistorico.barColor, subtipoAtendimento?.tagColor), [
    theme,
    tipoAtendimentoHistorico.barColor,
    subtipoAtendimento,
  ])

  const escutaInicialTagEnabled =
    (tipoAtendimento === TipoAtendimentoProfissional.ESCUTA_INICIAL ||
      tipoAtendimento === TipoAtendimentoHistoricoClinico.ESCUTA_INICIAL) &&
    !!historico.classificacaoRisco

  const vacinacaoTagEnabled =
    tipoAtendimento === TipoAtendimentoProfissional.VACINACAO ||
    tipoAtendimento === TipoAtendimentoHistoricoClinico.VACINACAO

  return (
    <AccordionItem css={classes.accordionItem} dangerouslySetExpanded={isVisitaAusenteOuRecusada ? false : undefined}>
      <Tooltip
        text={
          isVisitaAusenteOuRecusada &&
          `${
            accordionBloqueadoMensagem[subtipoAtendimento.nome]
          } no momento da visita. Não há detalhes a serem exibidos`
        }
      >
        <div>
          <AccordionItemButton css={classes.accordionButton}>
            <div css={classes.barColor} />
            <div css={classes.accordionButtonContent}>
              <HFlow>
                <Icon name='accordionAngleIcon' icon='angleDown' size={1.5} />
                <VFlow vSpacing={0.5}>
                  <HFlow
                    style={css`
                      display: inline-flex;
                    `}
                    alignItems='center'
                    hSpacing={0.5}
                  >
                    <Icon
                      name='iconTipoConsulta'
                      icon={tipoAtendimentoHistorico.icon}
                      color={tipoAtendimentoHistorico.iconColor}
                      size={2}
                    />
                    <Text
                      fontWeight='bold'
                      fontSize={1}
                      style={css`
                        color: ${tipoAtendimentoHistorico.iconColor};
                      `}
                    >
                      {tipoAtendimentoHistorico.descricao}
                    </Text>
                    {horaOrTurnoByOrigem && (
                      <Fragment>
                        <Text
                          fontSize={1}
                          style={css`
                            color: ${tipoAtendimentoHistorico.iconColor};
                          `}
                        >
                          |
                        </Text>
                        <Text
                          fontSize={1}
                          style={css`
                            color: ${tipoAtendimentoHistorico.iconColor};
                          `}
                        >
                          {horaOrTurnoByOrigem?.capitalize()}
                        </Text>
                      </Fragment>
                    )}
                    {escutaInicialTagEnabled && (
                      <Tag style={recordEscutaHistoricoTagStyle[historico.classificacaoRisco].style}>
                        {recordEscutaHistoricoTagStyle[historico.classificacaoRisco].descricao}
                      </Tag>
                    )}
                    {subtipoAtendimento && <Tag style={classes.tags}>{SubtipoRecord[subtipoAtendimento.nome]}</Tag>}
                    {historico.idadeGestacional && (
                      <Tag style={classes.tags}>
                        {`${pluralizeAndConcatValue(
                          historico.idadeGestacional.semanas,
                          'semana'
                        )} e ${pluralizeAndConcatValue(historico.idadeGestacional.dias, 'dia')}`}
                      </Tag>
                    )}
                  </HFlow>
                  <Text>
                    {`${historico.profissional?.nome} - ${historico.cbo.nome.capitalize()} - ${historico.cbo.cbo2002} ${
                      historico.equipe
                        ? `| 
                  Equipe ${historico.equipe?.ine} - ${historico.equipe?.nome}`
                        : ''
                    }`}
                  </Text>
                  {(tipoAtendimento === TipoAtendimentoHistoricoClinico.ATENDIMENTO_DOMICILIAR ||
                    tipoAtendimento === TipoAtendimentoHistoricoClinico.AVALIACAO_ELEGIBILIDADE) &&
                    hasCondicoesAvaliadas && (
                      <HFlow hSpacing={0.2}>
                        <div css={classes.condicoesAvaliadas}>
                          <Text fontWeight='bold'>Condições avaliadas: </Text>
                          {historico?.condicoesAvaliadas && stringGrouper(...historico.condicoesAvaliadas)}
                        </div>
                      </HFlow>
                    )}
                  {dadosClinicosHeader && <div css={classes.dadosClinicos}>{dadosClinicosHeader}</div>}
                  {hasCondicoesAvaliadas && (
                    <Tooltip text='Mais problemas avaliados'>
                      <Icon size={1} icon='infoCircleOutline' style={classes.problemaAvaliado} />
                    </Tooltip>
                  )}
                  {hasSomeSubmodulesPlano(historico) && (
                    <HFlow>
                      {historico.hasPrescricaoMedicamento && <Tag type='normal'>Prescrição de medicamentos</Tag>}
                      {historico.hasAlergia && <Tag type='normal'>Alergia</Tag>}
                      {historico.hasAtestado && <Tag type='normal'>Atestado</Tag>}
                      {historico.hasOrientacao && <Tag type='normal'>Orientação</Tag>}
                      {historico.hasLembrete && <Tag type='normal'>Lembrete</Tag>}
                      {historico.hasSolicitacaoExame && <Tag type='normal'>Solicitação de exames</Tag>}
                      {historico.hasResultadoExame && <Tag type='normal'>Resultado de exames</Tag>}
                    </HFlow>
                  )}
                  {vacinacaoTagEnabled && (
                    <HFlow hSpacing={0.5}>
                      {historico.condicoesVacinacao?.isViajante && <Tag type='normal'>Viajante</Tag>}
                      {historico.condicoesVacinacao?.isGestante && <Tag type='normal'>Gestante</Tag>}
                      {historico.condicoesVacinacao?.isPuerpera && <Tag type='normal'>Puerpera</Tag>}
                      {historico.condicoesVacinacao?.isComunicanteHanseniase && (
                        <Tag type='normal'>Comunicante de hanseníase</Tag>
                      )}
                    </HFlow>
                  )}
                </VFlow>
              </HFlow>
              <div>
                <Button size='small' skin='ghost' disabled={isImpossibleToPrint}>
                  <Icon
                    icon='printerOutline'
                    size={1.5}
                    style={css`
                      float: right;
                    `}
                  />
                </Button>
              </div>
            </div>
          </AccordionItemButton>
        </div>
      </Tooltip>
      <AccordionItemState>
        {({ expanded }) =>
          expanded && (
            <AccordionItemPanel css={classes.accordionItemPanel}>
              <VFlow
                vSpacing={1}
                style={css`
                  padding: 1rem;
                `}
              >
                <HistoricoIdadeOrigemAtendimento
                  dataAtendimento={dataAtendimento}
                  origemAtendimento={historico.origemAtendimento}
                  dataNascimentoCidadao={dataNascimentoCidadao}
                />
                {tipoAtendimento === TipoAtendimentoHistoricoClinico.CONSUMO_ALIMENTAR && (
                  <HistoricoMarcadoresConsumoAlimentarPanel
                    cpfOuCnsCidadao={cpfOuCnsCidadao}
                    uuidAtend={historico.codigoUnicoRegistro}
                  />
                )}
                {tipoAtendimento === TipoAtendimentoHistoricoClinico.VACINACAO && (
                  <HistoricoVacinacaoPanel
                    cpfOuCnsCidadao={cpfOuCnsCidadao}
                    idAtendPec={historico.idAtendRecente}
                    uuidAtend={historico.codigoUnicoRegistro}
                    isAtendRecente={historico.isAtendRecente}
                  />
                )}
                {tipoAtendimento === TipoAtendimentoHistoricoClinico.ZIKA && (
                  <HistoricoZikaPanel
                    uuidAtend={historico?.codigoUnicoRegistro}
                    cpfOuCnsCidadao={historico?.cpfCnsCidadao}
                  />
                )}
                {tipoAtendimento === TipoAtendimentoHistoricoClinico.VISITA_DOMICILIAR && (
                  <HistoricoVisitaDomiciliarPanel
                    uuidAtend={historico?.codigoUnicoRegistro}
                    cpfOuCnsCidadao={historico?.cpfCnsCidadao}
                  />
                )}
                {tipoAtendimento === TipoAtendimentoHistoricoClinico.AVALIACAO_ELEGIBILIDADE && (
                  <HistoricoAvaliacaoElegibilidadePanel
                    uuidAtend={historico?.codigoUnicoRegistro}
                    cpfOuCnsCidadao={historico?.cpfCnsCidadao}
                  />
                )}
              </VFlow>
              <HistoricoInfoAdminPanel />
            </AccordionItemPanel>
          )
        }
      </AccordionItemState>
    </AccordionItem>
  )
}

const styles = (theme: Theme, barColor: string, tagColor: string) => {
  return {
    accordionItem: css`
      border: 1px solid ${barColor};
      border-radius: 2px;
      &:focus-within {
        outline: none;
        box-shadow: ${focusBoxShadow(theme, 'primary')};
        transition-property: box-shadow;
        transition-duration: 0.2s;
      }
    `,
    accordionButton: css`
      display: flex;
      cursor: pointer;
      border-radius: 2px;
      border: 0px;
      &:focus {
        outline: none;
        box-shadow: none;
      }
      &:hover {
        background-color: ${theme.pallete.gray.c90};
      }
      &[aria-expanded='true'] div:first-of-type {
        border-bottom-left-radius: 2.5px;
      }
      &[aria-expanded='true'] {
        border-bottom: 1px solid ${theme.pallete.gray.c80};
      }
      &[aria-expanded='false'] {
        border-bottom: 0px;
      }
      &[aria-expanded='true'] svg[name='accordionAngleIcon'],
      &[aria-selected='true'] svg[name='accordionAngleIcon'] {
        transform: rotate(180deg);
        transition: 0.4s;
      }
      &[aria-expanded='false'] svg[name='accordionAngleIcon'] {
        transform: rotate(0deg);
        transition: 0.4s;
      }
    `,
    accordionButtonContent: css`
      justify-content: space-between;
      background-color: ${theme.pallete.surface.background};
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
      padding: 1rem;
      display: flex;
      width: 100%;
    `,
    accordionItemPanel: css`
      flex: 1;
      background-color: ${theme.pallete.surface.main};
    `,
    barColor: css`
      width: 0.5rem;
      background-color: ${barColor};
      border-top-left-radius: 1px;
      border-bottom-left-radius: 1px;
    `,
    problemaAvaliado: css`
      color: ${theme.pallete.primary.main};
      display: flex;
      margin-right: 0.5rem;
    `,
    condicoesAvaliadas: css`
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
    `,
    dadosClinicos: css`
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      font-weight: bold;
    `,
    tags: css`
      color: ${theme.pallete.status.info.onColor};
      background-color: ${tagColor};
    `,
  }
}
